import { useEffect } from "react"
import { NextSeo } from "next-seo"
import Router, { useRouter } from "next/router"
import { Headers } from "node-fetch"

import image, { DIRECTOR_AVATAR, DIRECTOR_TOP } from "lacinetek-common/src/images"

import { useTranslation } from "lib/i18n/translation"
import { useCountry } from "lib/country"
import isBrowser from "lib/tools/is-browser"
import { BONUS_EXCLUSIFS, BONUS_ARCHIVES } from "lib/tools/product"
import { useMainCategories } from "lib/tools/categories"
import Gtm from "lib/gtm"
import { useLanguages } from "lib/languages"

import { Bonuses } from "components/common"
import BonusesQuery from "components/common/bonuses-query"
import {
	Header,
	List,
	LifeList,
	Availabilities
} from "components/list-of"

import CustomError from "../../_error"

const ListOf = ({ list }) => {
	const { c } = useTranslation("common")
	const { getLinks } = useLanguages()
	const { countrySlug, asPath } = useCountry()
	const router = useRouter()
	const url = router ? router.asPath : null
	const pageTitle = list.metaTitle !== ""
		? list.metaTitle
		: c("list-of", { director: list.name.replace(" | ", " ")})
	const baseBonuses = []
	let { inLifeList } = useMainCategories()

	if (list === undefined) {
		return <CustomError statusCode="404"/>
	}

	const director = list.director

	useEffect(() => {
		if (isBrowser) {
			const browserSlug = Router.asPath.split("/")[Router.asPath.split("/").length - 1]
			if (list.linkRewrite !== browserSlug) {
				const links = getLinks(Router.pathname, `/${countrySlug}/lists/${list.linkRewrite}`)
				Router.push(links.pathname, links.asPath, { shallow: true })
			}
		}
	}, [])

	return (
		<>
			<Gtm pageType="realisateur" list={list} pagePath={url} pageTitle={pageTitle} />
			<NextSeo
				title={pageTitle}
				description={list.metaDescription !== "" ? list.metaDescription : (list.descriptionShort !== "" ? list.descriptionShort : c("list-of", { director: list.name.replace(" | ", " ") }))}
				keywords={list.metaKeywords}
				canonical={asPath}
				openGraph={{
					type: "article",
					title: list.metaTitle !== "" ? list.metaTitle : c("list-of", { director: list.name.replace(" | ", " ")}),
					description: list.metaDescription !== "" ? list.metaDescription : c("list-of", { director: list.name.replace(" | ", " ")}),
					url: asPath,
					images: [{url: image(director, DIRECTOR_AVATAR, DIRECTOR_TOP)}],
					image: {url: image(director, DIRECTOR_AVATAR, DIRECTOR_TOP)},
				}}
			/>
			{ director
				? <BonusesQuery
						director={director.id}
						renderLoading={() => (
							<Content
								list={list}
								director={director}
								bonuses={baseBonuses}
								inLifeList={inLifeList(list)}
							/>
						)}
					>
						{ bonuses => (
								<Content
									list={list}
									director={director}
									bonuses={bonuses}
									inLifeList={inLifeList(list)}
								/>
						)}
					</BonusesQuery>
				: <Content
						list={list}
						director={{name: list.name.replace(" | ", " "), description: ""}}
						bonuses={[]}
						inLifeList={inLifeList(list)}
					/>
			}
		</>
	)
}

const Content = ({list, director, bonuses, loading, inLifeList}) => (
	<>
		<Header list={list} filmo={list.description} director={director} loading={loading} />
		{ inLifeList && <LifeList list={list} /> }
		<List list={list} director={director} loading={loading} />
		{ bonuses.length > 0 &&
			<>
				<Bonuses type={BONUS_EXCLUSIFS} bonuses={bonuses} director={director} loading={loading} />
				<Bonuses type={BONUS_ARCHIVES} bonuses={bonuses} director={director} loading={loading} />
			</>
		}
		{ inLifeList && <Availabilities director={director} loading={loading} /> }
	</>
)

export const getInitialProps = async (ctx) => {
	const domain = ctx.req ? `${ctx.req.protocol}://${ctx.req.get("Host")}` : ""
	const headers = new Headers()

	headers.append("language", ctx.req ? ctx.req.language : "")

	return {
		list: await fetch(`${domain}/fetch-list/${ctx.query.id}`, { headers }).then(res => res.json())
	}
}

ListOf.getInitialProps = getInitialProps

export default ListOf
